// ============================================================================
// HAI Reporting Tool - Authentication Context
// Manages user authentication state and provides auth methods
// ============================================================================

import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import {
  User as FirebaseUser,
  onAuthStateChanged,
  signOut as firebaseSignOut
} from 'firebase/auth';
import { doc, onSnapshot } from 'firebase/firestore';
import { auth, db } from '@/services/firebase';
import { User } from '@/types';
import { getUserProfile } from '@/services/auth.service';

interface AuthContextType {
  currentUser: FirebaseUser | null;
  userProfile: User | null;
  loading: boolean;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [currentUser, setCurrentUser] = useState<FirebaseUser | null>(null);
  const [userProfile, setUserProfile] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);

      if (user) {
        try {
          // Fetch user profile from Firestore
          console.log(`[AuthContext] User authenticated: ${user.uid}`);
          const profile = await getUserProfile(user.uid);
          console.log(`[AuthContext] User profile loaded. Facility ID:`, profile.facilityId);
          setUserProfile(profile);

          // Set up real-time listener for user profile changes
          // This ensures facility assignments update immediately when admin changes them
          const userDocRef = doc(db, 'users', user.uid);
          const unsubscribeSnapshot = onSnapshot(userDocRef, (docSnapshot) => {
            if (docSnapshot.exists()) {
              const data = docSnapshot.data();
              const updatedProfile: User = {
                id: docSnapshot.id,
                displayName: data.displayName,
                email: data.email,
                role: data.role,
                facilityId: data.facilityId || '',
                isActive: data.isActive,
                createdAt: data.createdAt?.toDate() || new Date(),
                updatedAt: data.updatedAt?.toDate() || new Date(),
                lastLoginAt: data.lastLoginAt?.toDate() || new Date()
              };
              console.log(`[AuthContext] Real-time profile update. Facility ID:`, updatedProfile.facilityId);
              setUserProfile(updatedProfile);
            }
          }, (error) => {
            console.error('[AuthContext] Error setting up profile listener:', error);
          });

          // Return the snapshot unsubscriber to be called on cleanup
          return unsubscribeSnapshot;
        } catch (error) {
          console.error('[AuthContext] Error fetching user profile:', error);
          setUserProfile(null);
        }
      } else {
        setUserProfile(null);
      }

      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const signOut = async () => {
    try {
      await firebaseSignOut(auth);
      setCurrentUser(null);
      setUserProfile(null);

      // Clear local IndexedDB cache on logout to prevent stale data
      try {
        const { db: localDb } = await import('@/services/db');
        await localDb.users.clear();
      } catch (error) {
        console.error('Error clearing local cache:', error);
      }
    } catch (error) {
      console.error('Error signing out:', error);
      throw error;
    }
  };

  const refreshProfile = async () => {
    if (!currentUser) return;
    try {
      const profile = await getUserProfile(currentUser.uid);
      setUserProfile(profile);
    } catch (error) {
      console.error('Error refreshing user profile:', error);
    }
  };

  const value: AuthContextType = {
    currentUser,
    userProfile,
    loading,
    signOut,
    refreshProfile
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}
